import { useState } from "react";

import { useSelector, useDispatch } from "react-redux";
import { userSelectors } from "../../../../../redux/user/userSelectors";
import { Checkbox } from "../../../../components/formComponents";
import { Loader } from "../../../../components/Loader/Loader";
import { MessageBox } from "../../../../components/MessageBox/MessageBox";
import { Button } from "../../../../components/buttons/Button";
import { serializeError } from "../../../../../utils/serializeError";
import { userApi } from "../../../../../api/userApi";
import { formAttribution } from "../../../../../utils/formAttribution";
import { linksConstants } from "../../../../../constants/linksConstants";
import { Modal } from "../../../../components/Modal/Modal";
import { productsCatalogApi } from "../../../../../api/productsCatalogApi";
import { settingsApi } from "../../../../../api/settingsApi";
import { capitalize } from "../../../../../utils/string";
import { FiArrowLeft } from "react-icons/fi";
import { sendGaEvent } from "../../../../../config/userTracking";
import { apiTemplatesCatalogApi } from "../../../../../api/apiTemplatesCatalogApi";
import { operationalDashboardApi } from "../../../../../api/operationalDashboardApi";
import { allUseCasesThunks } from "../../../../../redux/useCases/allUseCasesSlice";
import { evaluationUseCasesThunks } from "../../../../../redux/useCases/evaluationUseCasesSlice";
import { productionUseCasesThunks } from "../../../../../redux/useCases/productionUseCasesSlice";
import { createdUseCasesThunks } from "../../../../../redux/useCases/createdUseCasesSlice";
import { useRouter } from "next/router";
import { generatePath } from "../../../../layout/Navigation/utils";

const sendAnalyticsData = async userData => {
  // TODO: We might need more tracking on that? There is no info about which template has been
  // cloned or basically anything else than just basic info about API Request being made
  try {
    const dataToSend = {
      type: "Platform API Creation",
      formValues: formAttribution("Platform API Creation"),
    };
    dataToSend.formValues.email = userData.user.email;
    dataToSend.formValues.full_name = userData.user.name;
    dataToSend.formValues.company = userData.organization.name;

    await userApi.sendFrontForm(dataToSend);
  } catch (e) {
    console.log("Failed to send analytics data");
  }
};

export const CloneAPIModal = ({
  open,
  closeModal,
  id,
  name,
  description,
  variant = "FROM_TEMPLATES_CATALOG", // "FROM_TEMPLATES_CATALOG", "FROM_DATASETS_CATALOG", "FROM_CREATE_DATA_API"
  connectors,
}) => {
  const router = useRouter();
  const userData = useSelector(userSelectors.getUserData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const handleSubmit = async event => {
    event.preventDefault();

    try {
      setError(null);
      setIsLoading(true);

      const { data: regionsList } = await settingsApi.getOrganizationRegions();
      const regionId = regionsList?.[0]?.id ?? 1;

      let response;

      switch (variant) {
        case "FROM_TEMPLATES_CATALOG": {
          response = await apiTemplatesCatalogApi.cloneAPITemplate({
            apiTemplateId: id,
            name: name,
            regionId,
            description: description,
          });
          break;
        }
        case "FROM_DATASETS_CATALOG": {
          response = await productsCatalogApi.createApiFromProvider({
            providerId: id,
            name: name,
            regionId,
            description: description,
          });
          break;
        }
        case "FROM_CREATE_DATA_API": {
          response = await apiTemplatesCatalogApi.cloneAPITemplate({
            apiTemplateId: id,
            name: name === "" ? "New Data API" : name,
            regionId,
            description: description === "" ? description : "Default Data API description...",
          });
          const useCaseId = response.data.success.ops_dash_use_case_id;
          const { data } = await operationalDashboardApi.getUseCase(useCaseId);
          const newChannel = data.channels[0];
          const channelId = newChannel.id;
          let newChannelData = JSON.parse(JSON.stringify(newChannel));
          connectors.forEach(connector => {
            const connectorName = connector.name;
            const connectorToAdd = { [connectorName]: { version: "$latest" } };
            const newProviders = { ...newChannelData.configuration.providers, ...connectorToAdd };
            newChannelData.configuration.providers = newProviders;
          });
          await operationalDashboardApi.updateUseCase(useCaseId, {
            ops_dash_use_case: {
              description: description || "Default Data API description...",
            },
          });
          await operationalDashboardApi.updateChannel(channelId, {
            ...newChannel,
            configuration: newChannelData.configuration,
          });
          break;
        }
      }

      sendAnalyticsData(userData, variant);

      sendGaEvent(
        {
          action: `Create Data API from ${
            variant === "FROM_DATASETS_CATALOG" ? "Connector" : "Recipe"
          } "${name}"`,
          category: "Create Data API",
        },
        userData?.user?.email,
      );

      dispatch(
        allUseCasesThunks.fetchAllUseCases({
          forceFetch: true,
        }),
      );
      dispatch(
        evaluationUseCasesThunks.fetchEvaluationUseCases({
          forceFetch: true,
        }),
      );
      dispatch(
        productionUseCasesThunks.fetchProductionUseCases({
          forceFetch: true,
        }),
      );
      dispatch(
        createdUseCasesThunks.fetchCreatedUseCases({
          forceFetch: true,
        }),
      );

      router.push({
        pathname: generatePath(linksConstants.USE_CASE_OVERVIEW, {
          useCaseId: response.data.success.ops_dash_use_case_id,
        }),
        state: { origin: "clone_api" },
      });
    } catch (e) {
      setError(getResponseErrors(e));
    } finally {
      setIsLoading(false);
    }
  };

  const getResponseErrors = e => {
    const options = {
      returnFullDataObject: true,
      includeSourceError: false,
    };
    const msg = serializeError(e, options).message;
    if (serializeError(e, options).status === 400) {
      return [msg.error];
    }
    if (msg.length !== 0 && serializeError(e, options).status === 422) {
      const errorCategories = Object.keys(msg);
      const errorArray = [];
      errorCategories.forEach(category => {
        const categoryNameCapitalized = capitalize(category);
        const errorSubjects = Object.keys(msg[category].errors);
        errorSubjects.forEach(subject => {
          const errorMessage = msg[category].errors[subject];
          errorArray.push(`${categoryNameCapitalized} ${subject} ${errorMessage}`);
        });
      });
      return errorArray;
    } else {
      return null;
    }
  };

  return (
    <Modal open={open} onBackdropClick={closeModal}>
      <div className="CloneAPIModal__modal">
        <Button
          onClick={closeModal}
          variant="noBorder"
          className="CloneAPIModal__modal__closeButton"
        >
          <FiArrowLeft />
          Back
        </Button>
        <h3 className="CloneAPIModal__modal__header">Create Data API</h3>
        {variant === "FROM_CREATE_DATA_API" && (
          <p className="CloneAPIModal__modal__paragraph">
            Please agree to the following in order to add this connector to a new evaluation Data
            API and start running transactions.
          </p>
        )}
        {variant !== "FROM_CREATE_DATA_API" && (
          <p className="CloneAPIModal__modal__paragraph">
            Please agree to the following in order to clone this{" "}
            {variant === "FROM_TEMPLATES_CATALOG" ? "recipe" : "dataset"} to a new evaluation Data
            API and start running transactions.
          </p>
        )}
        {isLoading && (
          <div className="CloneAPIModal__loading">
            <Loader />
          </div>
        )}
        <form onSubmit={handleSubmit} className="CloneAPIModal__form">
          <div className="CloneAPIModal__form__checkboxWrapper">
            <Checkbox
              id={"agree"}
              label={
                "I agree that I will use this Data API for evaluation purposes only, and will not deploy this data API into a production environment."
              }
              required
            />
          </div>
          {error && (
            <div className="CloneAPIModal__form__error">
              <MessageBox>
                <ul>
                  {error.map(errorItem => (
                    <li key={errorItem}>{errorItem}</li>
                  ))}
                </ul>
              </MessageBox>
            </div>
          )}
          <div className="CloneAPIModal__form__actions">
            <Button type="submit">Create Evaluation API</Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
